import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import toast from "react-hot-toast";

import CloseIcon from "../ui/CloseIcon";
import {
  addMinutesToTime,
  addZero,
  datePlusDays,
  dateToMonth,
  declOfNum,
  getDateFString,
} from "../../lib/utils";
import Calendar from "../Calendar";
import TimePicker from "../TimePicker";
import TimePeriodPicker from "../TimePeriodPicker";
import searchStore from "../../store/searchStore";
// import { useCssVariable } from "../../hooks/UseCssVariable";

import "./bookingServicePeriodPopup.scss";

const multiple_period = {
  half_hour: 30,
  hour: 60,
  "15_min": 15,
  "10_min": 10,
};

function BookingServicePeriodPopup({
  service_id,
  add_id,
  name = "Название услуги",
  add_hour,
  service_count,
  service_time,
  service_time_from,
  service_time_to,
  service_time_from_time,
  service_time_to_time,
  service_personal_time = "exact",
  service_personal_time_value,
  service_technical_break_interval,
  pay_type,
  booking,
  isOpened,
  setIsOpened,
  onChangeCount,
  days,
  def_date = null, // date()
  def_time = null, // { hours: (int), minutes: (int) }
  def_duration = null, // (int)
  is_edit = false,
}) {
  const calcDuration = (
    service_time,
    service_time_from,
    service_time_from_time,
  ) => {
    let precalc =
      service_time == "1" && service_time_from?.[0] && service_time_from_time
        ? service_time_from_time
        : +service_count || 1;
    if (pay_type == "hour") {
      precalc *= 60;
    }
    return precalc;
  };

  const periodsRef = useRef(null);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const [selectedDate, setSelectedDate] = useState(
    def_date || searchStore.dateCheckin,
  );
  const [selectedTime, setSelectedTime] = useState(def_time);
  const [selectedDuration, setSelectedDuration] = useState(
    def_duration ||
      calcDuration(service_time, service_time_from, service_time_from_time),
  );
  const [secondDurationDate, setSecondDurationDate] = useState(null); // "хвост" выбранного периода, не поместившийся в текущий день
  const [secondDuration, setSecondDuration] = useState(0);
  const [technicalTime, setTechnicalTime] = useState(
    (service_technical_break_interval?.hours || 0) * 60 +
      service_technical_break_interval?.minutes || 0,
  );

  const [clickCounter, setClickCounter] = useState(0);
  const [calendarOpened, setCalendarOpened] = useState(false);
  const [timeOpened, setTimeOpened] = useState(false);
  const [durationOpened, setDurationOpened] = useState(false);

  useEffect(() => {
    setSelectedDate(def_date || searchStore.dateCheckin);
    setSelectedTime(def_time);
    setSelectedDuration(def_duration);
  }, [def_date, def_time, def_duration]);

  useEffect(() => {
    setTechnicalTime(
      (service_technical_break_interval?.hours || 0) * 60 +
        service_technical_break_interval?.minutes || 0,
    );
  }, [service_technical_break_interval]);

  // скролл к выбранной дате
  useEffect(() => {
    setSecondDurationDate(null);
    setSecondDuration(0);
    if (isOpened && periodsRef.current) {
      // Небольшая задержка для гарантии что DOM полностью обновлен
      setTimeout(() => {
        scrollToHour("h_12");
      }, 50);
    }
  }, [isOpened]);

  const toggleCalendar = () => {
    setCalendarOpened((prev) => !prev);
  };

  const toggleTime = () => {
    setTimeOpened((prev) => !prev);
  };

  const toggleDuration = () => {
    setDurationOpened((prev) => !prev);
  };

  const closeAllOpened = () => {
    setCalendarOpened(false);
    setTimeOpened(false);
    setDurationOpened(false);
  };

  const setDateHandler = (dates) => {
    const dt = getDateFString(dates[0]);
    if (!checkingAvailability(dt, selectedTime, selectedDuration)) {
      return;
    }
    setSelectedDate(dt);
    const next_date = datePlusDays(dt, selectedDate < dt ? 1 : -1);
    scrollToDate(next_date > searchStore.dateCheckout ? dt : next_date);
    setTimeout(() => {
      scrollToHour(`h_${selectedTime?.hours}`);
    }, 400);
    setCalendarOpened(false);
  };

  const handleTimeSelected = (time) => {
    if (!checkingAvailability(selectedDate, time, selectedDuration)) {
      return;
    }
    setSelectedTime(time);
    scrollToHour(`h_${time.hours + 1}`);
    setTimeOpened(false);
  };

  const handleTimePeriodSelected = (time) => {
    const calctime = pay_type == "hour" ? time * 60 : time;
    if (!checkingAvailability(selectedDate, selectedTime, calctime)) {
      return;
    }
    setSelectedDuration(calctime);
    setDurationOpened(false);
  };

  // проверка указанного времени на занятость
  // return true - свободно
  const checkingAvailability = (date, time, duration) => {
    setSecondDuration(0);
    setSecondDurationDate(null);

    if (!date || !time) {
      // не заполнены все параметры - период ещё не выбран
      return true;
    }

    const dates = [{ date, time, duration }]; // сохраняем дату в массив
    // если период выходит за пределы дня, нужно проверять для двух дат

    // выход за пределы дня
    const endHour =
      (time.hours * 60 + duration + time.minutes + technicalTime) / 60;
    let second_duraion = 0;
    let next_date = null;

    if (endHour >= 24) {
      if (date == booking.tariff.end_date) {
        toast.error("Нельзя заказать услугу на следующие сутки после выезда");
        return false;
      } else {
        // добавляем "хвост" на следующий день
        const first_duration = (24 - time.hours) * 60 - time.minutes;
        second_duraion = duration - first_duration + technicalTime;
        next_date = datePlusDays(date, 1);
        dates.push({
          date: next_date,
          time: { hours: 0, minutes: 0 },
          duration: second_duraion,
        });
      }
    }

    for (const { date, time, duration } of dates) {
      // ищем дату в days
      const selected_day = days.find((f) => f.date == date);
      const selected_services = booking.selectedServicesWithDates.filter(
        (f) => f.id == service_id && f?.date == date,
      );

      if (!selected_day) {
        toast.error("Указанная дата выходит за пределы бронирования.");
        return false;
      }
      // смотрим reserverd[]
      if (
        selected_day?.reserved?.length == 0 &&
        selected_services?.length == 0
      ) {
        // в эту дату услуга не была заказана ни разу
        continue;
      }

      // Рассчитываем время окончания нового события в минутах
      const newStartMinutes = time.hours * 60 + time.minutes;
      const newEndMinutes = newStartMinutes + duration + technicalTime;

      // Проверяем каждое существующее событие
      for (const event of selected_day?.reserved || []) {
        const { h_start, m_start, duration } = event;

        // Рассчитываем время существующего события в минутах
        const existingStartMinutes = h_start * 60 + m_start;
        const existingEndMinutes = existingStartMinutes + duration;

        // Проверяем пересечение интервалов
        if (
          newStartMinutes < existingEndMinutes &&
          newEndMinutes > existingStartMinutes
        ) {
          toast.error("Указанный период занят. Выберите другой.");
          return false; // Найдено пересечение - время занято
        }
      }

      // проверка по выбранным периодам
      if (selected_services.length > 0) {
        for (const event of selected_services) {
          const { hour, minute, duration, technical_break } = event;

          // Если это редактирование и add_id совпадает - пропускаем проверку
          if (is_edit && add_id === event.add_id) {
            continue;
          }

          // Рассчитываем время существующего события в минутах
          const existingStartMinutes = hour * 60 + minute;
          const existingEndMinutes =
            existingStartMinutes + duration + technical_break;

          // Проверяем пересечение интервалов
          if (
            newStartMinutes < existingEndMinutes &&
            newEndMinutes > existingStartMinutes
          ) {
            toast.error("Указанный период занят. Выберите другой.");
            return false; // Найдено пересечение - время занято
          }
        }
      }
    }

    if (endHour >= 24) {
      setSecondDuration(second_duraion);
      setSecondDurationDate(next_date);
    }

    return true; // Пересечений не найдено - время свободно
  };

  const closeHandler = (e) => {
    e.stopPropagation();
    setIsOpened(false);
  };

  const addHandler = () => {
    const new_service_item = {
      date: selectedDate,
      hour: selectedTime.hours,
      minute: selectedTime.minutes,
      duration: selectedDuration,
      technical_break: technicalTime,
    };

    const calc_end_time = addMinutesToTime(
      new_service_item.hour,
      new_service_item.minute,
      new_service_item.duration + new_service_item.technical_break,
    );
    const calc_end_time_p = addMinutesToTime(
      new_service_item.hour,
      new_service_item.minute,
      new_service_item.duration,
    );
    new_service_item.h_end = calc_end_time.hours;
    new_service_item.m_end = calc_end_time.minutes;
    // без учета технического перерыва
    new_service_item.h_end_p = calc_end_time_p.hours;
    new_service_item.m_end_p = calc_end_time_p.minutes;
    new_service_item.id = service_id;
    if (is_edit) {
      new_service_item.add_id = add_id;
    }

    onChangeCount(1, new_service_item);
    setSelectedTime(null);
    setIsOpened(false);
  };

  const scrollToHour = (hourId) => {
    if (periodsRef.current) {
      const element = periodsRef.current.querySelector(`#${hourId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const scrollToDate = (date) => {
    if (periodsRef.current) {
      const element = periodsRef.current.querySelector(`#dt_${date}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // выбор периода кликом на шахматку
  const clickPeriodHandler = (e) => {
    let date = e.target.dataset?.date;
    let hour = +e.target.dataset?.hour;
    let time = { hours: hour, minutes: 0 };
    let duration = selectedDuration;
    let cl_counter = clickCounter; // счетчик кликов, для редактирования выбранного периода
    const init_duration = calcDuration(
      service_time,
      service_time_from,
      service_time_from_time,
    );

    if (!date || hour === undefined) {
      return;
    }

    if (
      date == selectedDate &&
      selectedTime?.hours !== undefined &&
      cl_counter < 2
    ) {
      if (selectedTime.hours < time.hours) {
        // увеличиваем длительность услуги
        let newDuration = time.hours + 1 - selectedTime.hours;
        if (pay_type == "hour" && add_hour == 0) {
          // выбор только кратно продолжительности, но не более максимального
          const remainder = newDuration % +service_count;
          if (remainder > 0) {
            newDuration += +service_count - remainder;
          }
        }

        if (service_time_to?.[0]) {
          if (
            (pay_type == "minute" && newDuration * 60 > service_time_to_time) ||
            (pay_type == "hour" && newDuration > service_time_to_time)
          ) {
            newDuration = service_time_to_time;
          }
        }

        duration = pay_type != "minute" ? newDuration * 60 : newDuration;

        cl_counter = 2;
        time = selectedTime;
      } else if (selectedTime.hours == time.hours) {
        // сбрасываем выбранный период
        time = null;
        duration = init_duration;
        cl_counter = 1;
      } else {
        // изменен час начала услуги
        duration = init_duration;
        cl_counter = 1;
      }
    } else {
      duration = init_duration;
      cl_counter = 1;
    }

    if (!checkingAvailability(date, time, duration)) {
      return;
    }

    setSelectedDate(date);
    setSelectedTime(time);
    setSelectedDuration(duration);
    setClickCounter(cl_counter);
  };

  const clearSelectedPeriod = () => {
    setSelectedTime(null);
    setSelectedDuration(
      calcDuration(service_time, service_time_from, service_time_from_time),
    );
  };

  const printSelectedPeriod = () => {
    const minutesToPx = 32 / 60;
    const endHour =
      (selectedTime.hours * 60 +
        selectedDuration +
        selectedTime.minutes +
        technicalTime) /
      60;

    let first_duration = selectedDuration + technicalTime;
    let second_duraion = 0;

    if (endHour >= 24) {
      first_duration = (24 - selectedTime.hours) * 60 - selectedTime.minutes;
      second_duraion = selectedDuration - first_duration + technicalTime;
    }

    return (
      <div
        className="bspcPlank selected"
        style={{
          gridRow: `${selectedTime.hours + 2}`,
          height: `${minutesToPx * first_duration}px`,
          marginTop: `${minutesToPx * selectedTime.minutes}px`,
        }}
        onClick={clearSelectedPeriod}
      >
        &nbsp;
      </div>
    );
  };

  if (!isOpened) {
    return null;
  }

  return (
    <>
      <div className="overlay light beautyScroll" onClick={closeHandler}></div>
      <div className="bookingServicePeriodWrap">
        {(calendarOpened || timeOpened || durationOpened) && (
          <div className="bspOverlay" onClick={closeAllOpened}></div>
        )}
        <div className="tar">
          <span onClick={closeHandler} className="curp">
            <CloseIcon w={18} h={18} />
          </span>
        </div>
        <h2>{name}</h2>
        <div className="bookingServicePeriodHeader">
          <div className="bookingServicePeriodOptions">
            <div className="bsWrap" onClick={toggleCalendar}>
              <span>Дата</span>
              <div className="bsInput">{dateToMonth(selectedDate, true)}</div>
            </div>
            <div className="bsWrap" onClick={toggleTime}>
              {selectedTime && <span>Начало</span>}
              <div className="bsInput">
                {selectedTime
                  ? `${addZero(selectedTime.hours)}:${addZero(
                      selectedTime.minutes,
                    )}`
                  : "Начало"}
              </div>
            </div>
            <div className="bsWrap" onClick={toggleDuration}>
              {selectedDuration && <span>Длительность</span>}
              <div className="bsInput">
                {selectedDuration
                  ? declOfNum(selectedDuration, ["минута", "минуты", "минут"])
                  : "Длительность"}
              </div>
            </div>
            {calendarOpened && (
              <div className="bspSelectPopup">
                <Calendar
                  minDt={new Date(searchStore.dateCheckin)}
                  maxDt={new Date(searchStore.dateCheckout)}
                  date1={new Date(selectedDate)}
                  showSelectedDates={false}
                  singleDateSelect={true}
                  singleDateHighlightOnly={true}
                  allTime={false}
                  onSelectDates={setDateHandler}
                />
              </div>
            )}
            {timeOpened && (
              <div className="bspSelectPopup time">
                <TimePicker
                  initialTime={{
                    hours: selectedTime?.hours || 16,
                    minutes: selectedTime?.minutes || 0,
                    step:
                      service_personal_time == "exact"
                        ? 1
                        : multiple_period[service_personal_time_value],
                  }}
                  onTimeSelected={handleTimeSelected}
                />
              </div>
            )}
            {durationOpened && (
              <div className="bspSelectPopup duration">
                <TimePeriodPicker
                  initialTime={
                    pay_type == "hour"
                      ? selectedDuration / 60
                      : selectedDuration
                  }
                  onTimeSelected={handleTimePeriodSelected}
                  min={
                    service_time == "1" &&
                    service_time_from?.[0] &&
                    service_time_from_time
                      ? service_time_from_time
                      : service_count
                  }
                  max={
                    service_time == "1" &&
                    service_time_to?.[0] &&
                    service_time_to_time
                      ? service_time_to_time
                      : 24
                  }
                  multiply={add_hour == 0 ? service_count || 1 : 1}
                  unit={pay_type}
                />
              </div>
            )}
          </div>
          <div>
            <button
              className="grayButton mini orange"
              type="button"
              onClick={addHandler}
              disabled={!selectedTime || !selectedDuration || !selectedDate}
            >
              {is_edit ? "Сохранить" : "Заказать"}
            </button>
          </div>
        </div>
        <div className="bookingServicePeriodContent mt24" ref={periodsRef}>
          <div className="bspcTimeline">
            <div className="bspcTime first">&nbsp;</div>
            {hours.map((hour) => (
              <div key={hour} className="bspcTime" id={`h_${hour}`}>
                {`${hour.toString().padStart(2, "0")}:00`}
              </div>
            ))}
          </div>
          {days?.length > 0 &&
            days.map((day, dindex) => (
              <div
                key={`day_${day.date}`}
                id={`dt_${day.date}`}
                className={classNames("bspcDay", { weekend: day.weekend })}
              >
                <div
                  className={classNames("bspcTitle", { first: dindex == 0 })}
                >
                  {dateToMonth(day.date, true)}
                </div>
                {hours.map((hour) => (
                  <div
                    key={`${day.date}_${hour}`}
                    data-date={day.date}
                    data-hour={hour}
                    className="bspcCeil"
                    onClick={clickPeriodHandler}
                  >
                    &nbsp;
                  </div>
                ))}
                {day?.reserved?.length > 0 &&
                  day.reserved.map((r, r_ind) => (
                    <div
                      key={`p_${r_ind}${r.h_start}${r.duration}`}
                      className="bspcPlank"
                      style={{
                        gridRow: `${r.h_start + 2}`,
                        height: `${(32 / 60) * r.duration}px`,
                        marginTop: `${(32 / 60) * r.m_start}px`,
                      }}
                    >
                      &nbsp;
                    </div>
                  ))}
                {selectedDate == day.date &&
                  selectedTime &&
                  selectedDuration > 0 &&
                  printSelectedPeriod()}
                {secondDurationDate == day.date && secondDuration > 0 && (
                  <div
                    className="bspcPlank selected"
                    style={{
                      gridRow: `${0 + 2}`,
                      height: `${(32 / 60) * secondDuration}px`,
                      marginTop: `0px`,
                    }}
                    onClick={clearSelectedPeriod}
                  >
                    &nbsp;
                  </div>
                )}
                {booking.selectedServicesWithDates
                  .filter(
                    (f) =>
                      f.id == service_id &&
                      f?.date == day.date &&
                      (!is_edit || f.add_id != add_id),
                  )
                  .map((ss) => (
                    <div
                      className="bspcPlank selected"
                      style={{
                        gridRow: `${ss.hour + 2}`,
                        height: `${(32 / 60) * ss.duration}px`,
                        marginTop: `${(32 / 60) * ss.minute}px`,
                      }}
                    >
                      &nbsp;
                    </div>
                  ))}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

export default BookingServicePeriodPopup;
